// FavoriteSubjectStep.js - Favorite STEM subject selection step
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const SUBJECTS = [
  { id: 'physics', label: 'Physics', icon: 'atom', color: '#3b82f6' },
  { id: 'chemistry', label: 'Chemistry', icon: 'flask', color: '#8b5cf6' },
  { id: 'biology', label: 'Biology', icon: 'dna', color: '#10b981' },
  { id: 'math', label: 'Mathematics', icon: 'calculator-variant', color: '#f59e0b' },
  { id: 'computer_science', label: 'Computer Science', icon: 'laptop', color: '#06b6d4' },
  { id: 'earth_science', label: 'Earth Science', icon: 'earth', color: '#84cc16' },
];

const FavoriteSubjectStep = ({ value = '', onChange, onNext, onBack }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [subject, setSubject] = useState(value);

  useEffect(() => {
    setSubject(value);
  }, [value]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#10b981',
    border: isDark ? '#475569' : '#e2e8f0',
  };

  const selectSubject = (subjectId) => {
    setSubject(subjectId);
    onChange?.(subjectId);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: colors.accent + '15' }]}>
          <MaterialCommunityIcons name="flask" size={48} color={colors.accent} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t("What's your favorite STEM subject?")}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t("We'll highlight related content for you")}
        </Text>

        {/* Subject Grid */}
        <View style={styles.subjectsGrid}>
          {SUBJECTS.map((s) => {
            const isSelected = subject === s.id;
            return (
              <TouchableOpacity
                key={s.id}
                style={[
                  styles.subjectCard,
                  {
                    backgroundColor: isSelected ? s.color + '15' : colors.bg,
                    borderColor: isSelected ? s.color : colors.border,
                  },
                ]}
                onPress={() => selectSubject(s.id)}
                activeOpacity={0.7}
              >
                <View
                  style={[
                    styles.subjectIcon,
                    { backgroundColor: isSelected ? s.color + '25' : colors.border + '50' },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={s.icon}
                    size={28}
                    color={isSelected ? s.color : colors.textSecondary}
                  />
                </View>
                <Text
                  style={[
                    styles.subjectLabel,
                    { color: isSelected ? s.color : colors.text },
                  ]}
                >
                  {t(s.label)}
                </Text>
                {isSelected && (
                  <View style={[styles.checkBadge, { backgroundColor: s.color }]}>
                    <MaterialCommunityIcons name="check" size={14} color="#fff" />
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>{t('Back')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onNext}
            activeOpacity={0.8}
          >
            <Text style={styles.primaryButtonText}>
              {subject ? t('Continue') : t('Skip')}
            </Text>
            <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  iconContainer: {
    width: 88,
    height: 88,
    borderRadius: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  subjectsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 28,
    width: '100%',
  },
  subjectCard: {
    width: '30%',
    minWidth: 100,
    aspectRatio: 1,
    borderRadius: 16,
    borderWidth: 2,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  subjectIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  subjectLabel: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  checkBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default FavoriteSubjectStep;
